import { createContext } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import type { openCloseStates } from '../../hooks/useTransitionState';

import type { AppShellProps, UtilItemProps } from './AppShell.types';

export interface AppShellDrawerContextValue {
  drawerOpen: boolean;
  /** Name of the currently open drawer item; undefined when drawer is closed. */
  openItemName: string | undefined;
  openDrawer: (item: UtilItemProps) => void;
  closeDrawer: () => void;
  /** Replace the currently open drawer item with a fresh copy (e.g. after data loads). */
  refreshDrawer: (item: UtilItemProps) => void;
}

export const AppShellDrawerContext = createContext<AppShellDrawerContextValue>({
  drawerOpen: false,
  openItemName: undefined,
  openDrawer: () => {},
  closeDrawer: () => {},
  refreshDrawer: () => {}
});

export interface AppShellContextValue {
  navOpen: boolean;
  drawerOpen: boolean;
  setDrawerOpen: (val: boolean) => void;
  navState: (typeof openCloseStates)[keyof typeof openCloseStates];
  focusedImperatively: MutableRefObject<boolean>;
  headerEl: HTMLDivElement | null;
  appHeaderEl?: HTMLDivElement | null;
  searchContainerEl: HTMLElement | null;
  setSearchContainerEl: (el: HTMLElement | null) => void;
  collapsedHoverMenus?: AppShellProps['collapsedHoverMenus'];
  isMobileNav: boolean;
  previewActive: boolean;
  setPreviewActive: Dispatch<SetStateAction<boolean>>;
}

const AppShellContext = createContext<AppShellContextValue>({
  navOpen: false,
  drawerOpen: false,
  setDrawerOpen: () => {},
  navState: 'closed',
  collapsedHoverMenus: false,
  focusedImperatively: {
    get current() {
      return false;
    }
  },
  headerEl: null,
  appHeaderEl: null,
  searchContainerEl: null,
  setSearchContainerEl: () => {},
  isMobileNav: false,
  previewActive: false,
  setPreviewActive: () => {}
});

export default AppShellContext;
